import { useState, useRef, useEffect, useLayoutEffect, useId, useCallback } from 'react'
import { useI18n } from '../../i18n/useI18n.js'
import { ChevronDown, Close, Check } from '../icons/index.js'
import { InfoTooltip } from '../InfoTooltip/index.js'
import styles from './MultiSelect.module.scss'

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeOptions(options) {
  return options.map((opt) =>
    typeof opt === 'string' || typeof opt === 'number'
      ? { value: String(opt), label: String(opt) }
      : { value: String(opt.value), label: opt.label },
  )
}

// ── MultiSelect ───────────────────────────────────────────────────────────────

/**
 * Custom multi-select with chip overflow indicator.
 *
 * Selected values appear as chips inside the trigger. When chips don't all fit,
 * a `+N` overflow badge shows how many are hidden. A `×` button clears all.
 * The dropdown shows all options; selected ones are marked with a check icon.
 *
 * Overflow detection: uses ResizeObserver + chip refs measured via
 * useLayoutEffect. The +N badge is always in the DOM (visibility:hidden when
 * unused) so its width is always available for accurate measurement.
 *
 * WP reset note: `:is([data-app='bim-cadaplus-app'].bim-app-root) li { padding: 0 }` has
 * specificity (1,1,1) — beats CSS module class (0,1,0). Padding on <li>
 * is applied via inline style.
 *
 * @param {object}   props
 * @param {string[]} props.value                  - Controlled array of selected values.
 * @param {(v: string[]) => void} props.onChange
 * @param {Array<string|{value:string, label:string}>} props.options
 * @param {'chips'|'summary'} [props.variant='chips'] - 'chips': overflow chip row (default).
 *                                                       'summary': label inside trigger + [N ×] chip.
 * @param {string}  [props.label]                 - Label shown inside trigger when variant='summary'.
 * @param {string}  [props.placeholder]           - Falls back to i18n 'common_ui.select_placeholder'
 * @param {boolean} [props.isActive=false]
 * @param {boolean} [props.disabled=false]
 * @param {string}  [props.id]
 * @param {string}  [props.name]
 * @param {string}  [props.className]
 * @param {boolean} [props.strictValue]      - When provided, renders a strict-match toggle inside the dropdown.
 * @param {(v: boolean) => void} [props.onStrictChange]
 * @param {string}  [props.strictTooltip]   - Tooltip text for the [?] icon next to the strict toggle label.
 * @param {string}  [props.infoTooltip]     - When provided, renders a read-only info row above options (label + icon, no checkbox).
 */
export function MultiSelect({
  value = [],
  onChange,
  options = [],
  variant = 'chips',
  label,
  placeholder,
  isActive = false,
  disabled = false,
  id,
  name,
  className,
  strictValue,
  onStrictChange,
  strictTooltip,
  infoTooltip,
}) {
  const { t } = useI18n()

  const resolvedPlaceholder = placeholder ?? t('common.select_placeholder')
  
  const fallbackId = useId()
  const fieldId = id ?? fallbackId

  const [isOpen, setIsOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const [hiddenCount, setHiddenCount] = useState(0)
  const [measureKey, setMeasureKey] = useState(0)  // bumped by ResizeObserver to re-trigger layout effect
  const [dropUp, setDropUp] = useState(false)

  const wrapperRef   = useRef(null)
  const chipsAreaRef = useRef(null)   // the scrollable/clipped chips row
  const chipRefs     = useRef([])     // array of chip DOM nodes
  const badgeRef     = useRef(null)   // the +N overflow badge node
  const dropdownRef  = useRef(null)   // the dropdown <ul> node

  const normalized  = normalizeOptions(options)
  const selectedSet = new Set(value)

  const isSummary = variant === 'summary'

  // Sync chip refs array length with value length.
  // Only relevant in chips mode — summary doesn't use chipRefs.
  if (!isSummary) {
    chipRefs.current = chipRefs.current.slice(0, value.length)
  }

  // ── Overflow measurement (chips mode only) ──────────────────────────────

  useLayoutEffect(() => {
    if (isSummary) return
    const area = chipsAreaRef.current
    if (!area) return

    if (value.length === 0) {
      setHiddenCount(0)
      return
    }

    // Badge is a flex sibling of chipsArea — its width is already excluded from
    // area.offsetWidth. Single pass: find the first chip whose right edge exceeds the area.
    const areaWidth = area.offsetWidth
    const chips = chipRefs.current
    let firstHiddenWithBadge = -1
    for (let i = 0; i < chips.length; i++) {
      const chip = chips[i]
      if (!chip) continue
      if (chip.offsetLeft + chip.offsetWidth > areaWidth) {
        firstHiddenWithBadge = i
        break
      }
    }

    setHiddenCount(firstHiddenWithBadge === -1 ? 0 : value.length - firstHiddenWithBadge)
  }, [isSummary, value, isOpen, measureKey]) // re-measure when value changes, list opens/closes, or area resizes

  // ResizeObserver (chips mode only): bump measureKey so useLayoutEffect re-runs.
  useEffect(() => {
    if (isSummary) return
    const area = chipsAreaRef.current
    if (!area) return
    const ro = new ResizeObserver(() => setMeasureKey((k) => k + 1))
    ro.observe(area)
    return () => ro.disconnect()
  }, [isSummary])

  // Flip dropdown above the wrapper if it would overflow the viewport bottom.
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return
    const rect = dropdownRef.current.getBoundingClientRect()
    setDropUp(rect.bottom > window.innerHeight - 8)
  }, [isOpen])

  // ── Helpers ───────────────────────────────────────────────────────────────

  const toggle = useCallback((optValue) => {
    if (selectedSet.has(optValue)) {
      onChange?.(value.filter((v) => v !== optValue))
    } else {
      onChange?.([...value, optValue])
    }
  }, [value, onChange, selectedSet])

  function clearAll(e) {
    e.stopPropagation()
    onChange?.([])
  }

  function removeChip(e, optValue) {
    e.stopPropagation()
    onChange?.(value.filter((v) => v !== optValue))
  }

  function openList() {
    if (disabled || normalized.length === 0) return
    setHighlighted(-1)
    setIsOpen(true)
  }

  function closeList() {
    setIsOpen(false)
    setHighlighted(-1)
  }

  // ── Click-outside ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return
    function onOutside(e) {
      if (!wrapperRef.current?.contains(e.target)) closeList()
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [isOpen])

  // ── Keyboard ──────────────────────────────────────────────────────────────

  function handleKeyDown(e) {
    if (disabled) return
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (!isOpen) openList()
        else if (highlighted >= 0) toggle(normalized[highlighted].value)
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!isOpen) openList()
        else setHighlighted((h) => Math.min(h + 1, normalized.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (isOpen) setHighlighted((h) => Math.max(h - 1, 0))
        break
      case 'Escape':
        if (isOpen) { e.preventDefault(); closeList() }
        break
      case 'Tab':
        if (isOpen) closeList()
        break
    }
  }

  const listId      = `${fieldId}-list`
  const visibleCount = value.length - hiddenCount

  // ── Summary trigger content ───────────────────────────────────────────────

  const summaryTrigger = isSummary ? (
    <div className={styles.summaryTrigger}>
      <span className={[styles.summaryLabel, value.length === 0 ? styles.summaryLabelMuted : ''].filter(Boolean).join(' ')}>
        {label ?? resolvedPlaceholder}
      </span>
      {value.length > 0 && (
        <span className={[styles.summaryChip, !isActive ? styles.summaryChipInactive : ''].filter(Boolean).join(' ')}>
          {value.length > 0 && <span className={styles.summaryChipCount}>{value.length}</span>}
          <button
            type="button"
            className={styles.summaryChipRemove}
            tabIndex={-1}
            aria-label="Wyczyść wybór"
            onMouseDown={(e) => { e.preventDefault(); clearAll(e) }}
          >
            <Close width="0.7em" height="0.7em" />
          </button>
        </span>
      )}
    </div>
  ) : null

  return (
    <div
      ref={wrapperRef}
      id={fieldId}
      className={[
        styles.wrapper,
        isOpen    ? styles.open     : '',
        isActive  ? styles.active   : '',
        disabled  ? styles.disabled : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls={listId}
      aria-multiselectable="true"
      tabIndex={disabled ? -1 : 0}
      style={{ outline: 0 }}
      onClick={() => (isOpen ? closeList() : openList())}
      onKeyDown={handleKeyDown}
    >
      {/* Hidden native select for form submission */}
      <select
        name={name ?? id}
        multiple
        value={value}
        onChange={() => {}}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
      >
        {normalized.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* ── Summary trigger (variant='summary') ─────────────────────────── */}
      {isSummary && summaryTrigger}

      {/* ── Chips area (variant='chips') ─────────────────────────────────── */}
      {!isSummary && (
        <div ref={chipsAreaRef} className={styles.chipsArea}>
          {value.length === 0 ? (
            <span className={styles.placeholder}>{resolvedPlaceholder}</span>
          ) : (
            value.map((v, i) => {
              const optLabel = normalized.find((o) => o.value === v)?.label ?? v
              const hidden   = i >= visibleCount
              return (
                <span
                  key={v}
                  ref={(el) => { chipRefs.current[i] = el }}
                  className={[styles.chip, hidden ? styles.chipHidden : ''].filter(Boolean).join(' ')}
                  aria-hidden={hidden ? 'true' : undefined}
                >
                  <span className={styles.chipLabel}>{optLabel}</span>
                  <button
                    type="button"
                    className={styles.chipRemove}
                    tabIndex={-1}
                    aria-label={`Usuń ${optLabel}`}
                    onMouseDown={(e) => { e.preventDefault(); removeChip(e, v) }}
                  >
                    <Close width="0.75em" height="0.75em" />
                  </button>
                </span>
              )
            })
          )}
        </div>
      )}

      {/* ── Overflow badge (chips mode only) ────────────────────────────────
           Sibling of chipsArea in the wrapper flex row, NOT inside it.
           Always rendered so its width is stable and constrains chipsArea.
           visibility:hidden when there's no overflow (takes space, stays invisible). */}
      {!isSummary && (
        <span
          ref={badgeRef}
          className={styles.overflowBadge}
          style={{ visibility: hiddenCount > 0 ? 'visible' : 'hidden' }}
          aria-hidden={hiddenCount > 0 ? undefined : 'true'}
          aria-label={hiddenCount > 0 ? `i ${hiddenCount} więcej` : undefined}
        >
          +{hiddenCount}
        </span>
      )}

      {/* ── Actions: clear-all + chevron ────────────────────────────────── */}
      <div className={styles.actions}>
        {!isSummary && isActive && value.length > 0 && (
          <button
            type="button"
            className={styles.clearAll}
            tabIndex={-1}
            aria-label="Wyczyść wybór"
            onMouseDown={(e) => { e.preventDefault(); clearAll(e) }}
          >
            <Close width="0.75em" height="0.75em" />
          </button>
        )}
        <span className={styles.chevron} aria-hidden="true">
          <ChevronDown />
        </span>
      </div>

      {/* ── Dropdown ────────────────────────────────────────────────────── */}
      {isOpen && (
        <ul
          ref={dropdownRef}
          role="listbox"
          id={listId}
          aria-multiselectable="true"
          className={[styles.dropdown, dropUp ? styles.dropUp : ''].filter(Boolean).join(' ')}
          onClick={(e) => e.stopPropagation()}
          onMouseLeave={() => setHighlighted(-1)}
        >          
        {/* ── Info row (optional) ─────────────────────────────────────── */}
          {infoTooltip && (
            <li className={styles.infoRow} role="presentation">
              <div className={styles.infoLabel}>
                <span>{t('catalog.filter_info_label')}</span>
                <InfoTooltip text={infoTooltip} placement="top" />
              </div>
              <hr className={styles.infoSeparator} />
            </li>
          )}
        {/* ── Strict-match toggle (optional) ──────────────────────────── */}
          {strictValue !== undefined && onStrictChange !== undefined && (
            <li className={styles.strictRow} role="presentation">
              <label className={styles.strictLabel}>
                <input
                  type="checkbox"
                  checked={strictValue}
                  className={styles.strictCheckbox}
                  onChange={(e) => onStrictChange(e.target.checked)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <span>{t('catalog.strict_match')}</span>
                {(strictTooltip) && (
                  <InfoTooltip text={strictTooltip} placement="top" />
                )}
              </label>
              <hr className={styles.strictSeparator} />
            </li>
          )}
          {normalized.length === 0 ? (
            <li className={styles.emptyMessage} role="option" aria-disabled="true" style={{ padding: '0.5em 0.75em' }}>
              {t('catalog.no_options')}
            </li>
          ) : normalized.map((opt, i) => {
            const selected = selectedSet.has(opt.value)
            return (
              <li
                key={opt.value}
                id={`${fieldId}-opt-${i}`}
                role="option"
                aria-selected={selected}
                className={[
                  styles.option,
                  selected           ? styles.optionSelected     : '',
                  i === highlighted  ? styles.optionHighlighted  : '',
                ].filter(Boolean).join(' ')}
                style={{ padding: '0.5em 0.75em' }}
                onMouseEnter={() => setHighlighted(i)}
                onMouseDown={(e) => { e.preventDefault(); toggle(opt.value) }}
              >
                <span className={styles.optionLabel}>{opt.label}</span>
                <span className={styles.optionCheck} aria-hidden="true">
                  {selected && <Check width="0.875em" height="0.875em" />}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
